import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import { CopyableId } from "../../shared/ui/CopyableId"
import {
  listOrdersApi,
  getOrderApi,
  OrderItem,
  OrderDetail,
  OrderStatus,
} from "../../features/payments/api/payments.api"
import {
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  AcademicCapIcon,
  CreditCardIcon,
  CalendarDaysIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const LIMIT = 20

const STATUS_CONFIG: Record<OrderStatus, { label: string; className: string; dot: string }> = {
  pending:  { label: "Ожидает",  className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",     dot: "bg-amber-400" },
  paid:     { label: "Оплачен",  className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", dot: "bg-emerald-400" },
  canceled: { label: "Отменён",  className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",        dot: "bg-gray-400" },
  expired:  { label: "Истёк",    className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",        dot: "bg-gray-400" },
  refunded: { label: "Возвращён", className: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",            dot: "bg-sky-400" },
}

const StatusBadge = ({ status }: { status: OrderStatus }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })

const formatMoney = (amount: number | undefined | null, currency: string | undefined | null) =>
  typeof amount === "number" ? `${amount.toLocaleString("ru-RU")} ${currency ?? ""}`.trim() : "—"

export const OrdersPage = () => {
  const [items, setItems] = useState<OrderItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  const [filterStatus, setFilterStatus] = useState<OrderStatus | "">("")
  const [userId, setUserId] = useState("")
  const [pendingUserId, setPendingUserId] = useState("")

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<OrderDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const offset = (page - 1) * LIMIT
    const res = await listOrdersApi({ limit: LIMIT, offset, status: filterStatus, user_id: userId || undefined })
    setItems(res.data.items)
    setTotal(res.data.total)
    setLoading(false)
  }, [page, filterStatus, userId])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [filterStatus, userId])

  const applyUserFilter = () => setUserId(pendingUserId.trim())
  const clearUserFilter = () => { setUserId(""); setPendingUserId("") }

  const openDetail = async (id: string) => {
    setSelectedId(id)
    setDetail(null)
    setDetailLoading(true)
    try {
      const res = await getOrderApi(id)
      setDetail(res.data)
    } finally {
      setDetailLoading(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <DashboardLayout title="Заказы">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          <FilterDropdown
            label="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={(Object.keys(STATUS_CONFIG) as OrderStatus[]).map((s) => ({ value: s, label: STATUS_CONFIG[s].label, dot: STATUS_CONFIG[s].dot }))}
          />

          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="ID пользователя…"
              value={pendingUserId}
              onChange={(e) => setPendingUserId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyUserFilter()}
              className="w-36 text-xs outline-none font-mono"
            />
          </div>
          <button onClick={applyUserFilter} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
            Найти
          </button>

          {(filterStatus || userId) && (
            <button
              type="button"
              onClick={() => { setFilterStatus(""); clearUserFilter() }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтры
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">{!loading && `${total} заказов`}</span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID пользователя</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Сумма</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                <th className="px-5 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5].map((j) => (
                      <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-16 text-center">
                    <ShoppingBagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Заказов не найдено</p>
                  </td>
                </tr>
              ) : (
                items.map((o) => (
                  <tr key={o.id} onClick={() => openDetail(o.id)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                    <td className="px-5 py-3.5"><CopyableId value={o.user_id} /></td>
                    <td className="px-5 py-3.5 font-medium text-gray-800">{formatMoney(o.total_amount, o.currency)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={o.status} /></td>
                    <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{formatDate(o.created_at)}</td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs font-medium text-gray-400 group-hover:text-blue-600 transition-colors">Открыть →</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Назад</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Вперёд →</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Detail drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${selectedId ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setSelectedId(null)}
          />
          <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${selectedId ? "translate-x-0" : "translate-x-full"}`}>
            {selectedId && (
              <>
                <div className="bg-gradient-to-r from-blue-500 to-indigo-400 px-6 pt-6 pb-8 flex-shrink-0 relative">
                  <button onClick={() => setSelectedId(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                    <XMarkIcon className="w-4 h-4 text-white" />
                  </button>
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingBagIcon className="w-5 h-5 text-white/80" />
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Заказ</span>
                  </div>
                  {detail && (
                    <>
                      <p className="text-white font-semibold text-xl leading-snug pr-10">{formatMoney(detail.total_amount, detail.currency)}</p>
                      <div className="mt-3"><StatusBadge status={detail.status} /></div>
                    </>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {detailLoading || !detail ? (
                    <div className="space-y-3">
                      {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
                    </div>
                  ) : (
                    <>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Детали</p>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-gray-400 flex items-center gap-1.5"><CreditCardIcon className="w-3.5 h-3.5" /> ID заказа</span>
                          <CopyableId value={detail.id} length={14} />
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-gray-400">ID пользователя</span>
                          <CopyableId value={detail.user_id} length={14} />
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-gray-400 flex items-center gap-1.5"><CalendarDaysIcon className="w-3.5 h-3.5" /> Создан</span>
                          <span className="text-xs text-gray-600">{formatDate(detail.created_at)}</span>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Состав заказа</p>
                        <div className="space-y-2">
                          {detail.items.length === 0 ? (
                            <p className="text-sm text-gray-400">Нет позиций</p>
                          ) : (
                            detail.items.map((line, i) => (
                              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                  <AcademicCapIcon className="w-4 h-4 text-violet-500" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-800 truncate">{line.title || line.target_id}</p>
                                  <p className="text-xs text-gray-400 capitalize">
                                    {line.target_type.replace("_", " ")}
                                    {line.quantity > 1 && ` · ${line.quantity} × ${formatMoney(line.unit_amount, detail.currency)}`}
                                  </p>
                                </div>
                                <span className="text-sm font-medium text-gray-700 flex-shrink-0">{formatMoney(line.subtotal, detail.currency)}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80">
                  <button onClick={() => setSelectedId(null)} className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                    Закрыть
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}